import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * KitschPopup - 앱 KitschPopup과 동일한 디자인의 웹 팝업 컴포넌트
 * @param {boolean} visible - 팝업 표시 여부
 * @param {string} title - 제목
 * @param {string} [message] - 본문 메시지
 * @param {React.ReactNode} [children] - 커스텀 콘텐츠 (인풋 등)
 * @param {string} [primaryButtonTitle='확인'] - 주 버튼 텍스트
 * @param {string} [secondaryButtonTitle] - 보조 버튼 텍스트 (없으면 버튼 1개만)
 * @param {function} [onPrimary] - 주 버튼 클릭
 * @param {function} [onSecondary] - 보조 버튼 클릭
 * @param {function} [onClose] - 배경 클릭 시 닫기
 */
const KitschPopup = ({
    visible,
    title,
    message,
    children,
    primaryButtonTitle = '확인',
    secondaryButtonTitle,
    onPrimary,
    onSecondary,
    onClose,
}) => {
    const handleBackdrop = () => {
        onClose?.() ?? onSecondary?.();
    };

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={handleBackdrop}
                    style={styles.overlay}
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        onClick={(e) => e.stopPropagation()}
                        style={styles.popup}
                    >
                        <h2 style={styles.title}>{title}</h2>

                        {message && <p style={styles.message}>{message}</p>}

                        {children}

                        <div style={styles.buttonRow}>
                            {secondaryButtonTitle && (
                                <>
                                    <motion.button
                                        whileTap={{ opacity: 0.8 }}
                                        onClick={onSecondary}
                                        style={styles.secondaryButton}
                                    >
                                        {secondaryButtonTitle}
                                    </motion.button>
                                    <div style={styles.buttonGap} />
                                </>
                            )}
                            <motion.button
                                whileTap={{ opacity: 0.8 }}
                                onClick={onPrimary}
                                style={styles.primaryButton}
                            >
                                {primaryButtonTitle}
                            </motion.button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        zIndex: 10001,
        padding: '0 40px',
    },
    popup: {
        width: '100%',
        maxWidth: '400px',
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        paddingTop: '28px',
        paddingBottom: '20px',
        paddingLeft: '20px',
        paddingRight: '20px',
    },
    title: {
        fontSize: '20px',
        fontFamily: 'Pretendard',
        fontWeight: 700,
        color: '#000000',
        letterSpacing: '-0.4px',
        textAlign: 'left',
        whiteSpace: 'pre-line',
        marginTop: 0,
        marginBottom: '8px',
        lineHeight: '1.4',
    },
    message: {
        fontSize: '15px',
        fontFamily: 'Pretendard',
        fontWeight: 500,
        color: '#666666',
        letterSpacing: '-0.3px',
        lineHeight: '22px',
        marginBottom: '24px',
        marginTop: 0,
    },
    buttonRow: {
        display: 'flex',
        flexDirection: 'row',
    },
    buttonGap: {
        width: '8px',
    },
    primaryButton: {
        flex: 1,
        padding: '14px 0',
        borderRadius: '10px',
        backgroundColor: '#FF7051',
        border: 'none',
        fontSize: '16px',
        fontFamily: 'Pretendard',
        fontWeight: 600,
        color: '#FFFFFF',
        letterSpacing: '-0.32px',
        cursor: 'pointer',
        textAlign: 'center',
    },
    secondaryButton: {
        flex: 1,
        padding: '14px 0',
        borderRadius: '10px',
        backgroundColor: '#F2F2F2',
        border: 'none',
        fontSize: '16px',
        fontFamily: 'Pretendard',
        fontWeight: 600,
        color: '#333333',
        letterSpacing: '-0.32px',
        cursor: 'pointer',
        textAlign: 'center',
    },
};

export default KitschPopup;
